import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import RequireAuth from '@/components/auth/RequireAuth';
import primelaneLogo from '@/assets/primelane-logo.png';

interface DriverLayoutContextValue {
    user: { email?: string | null } | null;
    handleSignOut: () => void;
}

// Export context so child pages can read user info
import { createContext, useContext } from 'react';
export const DriverLayoutContext = createContext<DriverLayoutContextValue | null>(null);
export const useDriverLayout = () => useContext(DriverLayoutContext);

/**
 * The chrome. It only ever renders inside `RequireAuth`, so `user` is present by the time
 * this component exists — the gate itself now lives in `@/components/auth/RequireAuth`, so
 * a route can be authenticated without also being wrapped in this header and padding.
 */
function DriverChrome() {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();

    const handleSignOut = async () => {
        await signOut();
        navigate('/auth');
    };

    return (
        <DriverLayoutContext.Provider value={{ user, handleSignOut }}>
            <div className="min-h-screen bg-background">
                {/* Mobile Header - only shows on small screens */}
                <header className="lg:hidden sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                    <div className="flex h-14 items-center px-4">
                        <img src={primelaneLogo} alt="Primelane" className="h-7" />
                    </div>
                </header>

                {/* Desktop Header - rendered by ContractDetail with nav */}
                <header className="hidden lg:block sticky top-0 z-50 border-b border-border bg-background" id="desktop-header">
                    <div className="flex h-16 items-center justify-between px-4 lg:px-6">
                        {/* Logo */}
                        <img src={primelaneLogo} alt="Primelane" className="h-8" />

                        {/* Center nav slot - filled by Outlet page via portal */}
                        <div id="desktop-nav-slot" />

                        {/* User & Sign Out */}
                        <div className="flex items-center gap-4">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="flex items-center gap-2 px-2 text-sm font-normal text-muted-foreground hover:text-foreground">
                                        <span className="hidden sm:inline">{user?.email || user?.phone || 'Account'}</span>
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-[200px]">
                                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 focus:text-red-600">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Sign Out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-6">
                    <Outlet />
                </main>
            </div>
        </DriverLayoutContext.Provider>
    );
}

export default function DriverLayout() {
    return (
        <RequireAuth>
            <DriverChrome />
        </RequireAuth>
    );
}
